import React from 'react'
import PropTypes from 'prop-types'
import { DateTime } from 'luxon'
import Avatar from '@material-ui/core/Avatar'
import Chip from '@material-ui/core/Chip'
import Typography from '@material-ui/core/Typography'
import FlagIcon from 'components/material-symbol/icons/Flag'
import PlaceIcon from 'components/material-symbol/icons/Place'
import ScheduleIcon from 'components/material-symbol/icons/Schedule'
import CalendarTodayIcon from 'components/material-symbol/icons/CalendarToday'
import { getInitials } from 'lib/text-display'
import { durationText } from 'utils/durationUtils'
import { formatCurrency } from '../../utils'
import useStyles from './styles'

// Mirrors when CoachLogo shows a crest: QE / no member type shows nothing,
// AP / ST always show, everything else needs a certification level.
export const getMemberLabel = ({ memberType, certificationLevel } = {}) => {
  if (!memberType || memberType === 'QE') return null
  if (memberType === 'AP') return 'PGA Associate'
  if (memberType === 'ST') return 'PGA Student'
  return certificationLevel ? 'PGA Member' : null
}

// Lesson pack offerings show their prices on the pack step, not here. Free
// lessons read "Free" instead of "$0.00".
export const getPriceLabel = lessonType => {
  const { priceInCents, lessonPacks } = lessonType
  if (lessonPacks?.length > 0 || priceInCents == null) return null
  if (priceInCents === 0) return 'Free'
  return formatCurrency(priceInCents / 100)
}

export const formatSelectedSlot = (selectedSlot, timezone) =>
  DateTime.fromISO(selectedSlot, { zone: timezone })
    .setLocale('en-US')
    .toFormat("ccc, LLL d '·' h:mm a")

const CoachAvatar = ({ coach, small }) => {
  const classes = useStyles()
  const className = small
    ? `${classes.avatar} ${classes.avatarSmall}`
    : classes.avatar
  const photo = coach.coachProfile?.profilePhoto
  return photo ? (
    <Avatar className={className} alt={coach.name} src={photo} />
  ) : (
    <Avatar className={className}>{getInitials(coach.name)}</Avatar>
  )
}

CoachAvatar.propTypes = {
  coach: PropTypes.object.isRequired,
  small: PropTypes.bool,
}

export const MobileBookingSummary = ({
  lessonType,
  coach,
  facility: chosenFacility,
}) => {
  const classes = useStyles()
  const facility = chosenFacility || lessonType.facilities?.[0]
  const priceLabel = getPriceLabel(lessonType)
  const subtitle = [facility?.name, `${lessonType.durationInMinutes} min`]
    .filter(Boolean)
    .join(' · ')
  return (
    <div className={classes.mobileSummary}>
      <CoachAvatar coach={coach} small />
      <div className={classes.mobileSummaryText}>
        <p className={classes.mobileSummaryTitle}>
          {lessonType.title} · {coach.name}
        </p>
        <p className={classes.mobileSummarySub}>{subtitle}</p>
      </div>
      {priceLabel && <span className={classes.mobilePrice}>{priceLabel}</span>}
    </div>
  )
}

MobileBookingSummary.propTypes = {
  lessonType: PropTypes.object.isRequired,
  coach: PropTypes.object.isRequired,
  facility: PropTypes.object,
}

const BookingSummaryCard = ({ lessonType, coach, selectedSlot, timezone }) => {
  const classes = useStyles()
  const facility = lessonType.facilities?.[0]
  const memberLabel = getMemberLabel(coach.coachProfile)
  const priceLabel = getPriceLabel(lessonType)
  const cityState = [facility?.city, facility?.state].filter(Boolean).join(', ')

  return (
    <aside className={classes.summaryCard} aria-label="Booking summary">
      <div className={classes.coachRow}>
        <CoachAvatar coach={coach} />
        <div>
          <Typography variant="h6" component="p" className={classes.coachName}>
            {coach.name}
          </Typography>
          {memberLabel && (
            <span className={classes.coachTitle}>
              <Chip
                size="small"
                label={memberLabel}
                className={classes.memberChip}
              />
            </span>
          )}
        </div>
      </div>
      <hr className={classes.summaryDivider} />
      <Typography variant="h5" component="h2" className={classes.lessonTitle}>
        {lessonType.title}
      </Typography>
      <ul className={classes.facts}>
        {facility?.name && (
          <li className={classes.fact}>
            <FlagIcon aria-hidden="true" />
            <span>{facility.name}</span>
          </li>
        )}
        {cityState && (
          <li className={classes.fact}>
            <PlaceIcon aria-hidden="true" />
            <span>{cityState}</span>
          </li>
        )}
        <li className={classes.fact}>
          <ScheduleIcon aria-hidden="true" />
          <span>{durationText(lessonType.durationInMinutes)}</span>
        </li>
        <li className={classes.fact}>
          <CalendarTodayIcon aria-hidden="true" />
          <span>
            {selectedSlot
              ? formatSelectedSlot(selectedSlot, timezone)
              : 'Pick a time'}
          </span>
        </li>
      </ul>
      {priceLabel && (
        <>
          <hr className={classes.summaryDivider} />
          <div className={classes.totalRow}>
            <span className={classes.totalLabel}>Total</span>
            <Typography
              variant="h5"
              component="span"
              className={classes.totalValue}
            >
              {priceLabel}
            </Typography>
          </div>
        </>
      )}
    </aside>
  )
}

BookingSummaryCard.propTypes = {
  lessonType: PropTypes.object.isRequired,
  coach: PropTypes.object.isRequired,
  selectedSlot: PropTypes.string,
  timezone: PropTypes.string,
}

export default BookingSummaryCard
